import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpEvent, HttpEventType, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as cryptoJS from 'crypto-js';
import 'devextreme/data/odata/store';
import jwt_decode from 'jwt-decode';
import { BehaviorSubject, Observable, Subject, throwError } from "rxjs";
import { tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { HeaderEnum, ResponseStatusEnum } from '../enumerators/enumerators.enum';
import { AlertEnum } from "../enumerators/messages.enum";
import { ServiceResponse } from "../models/service-response.model";
import { Security } from '../utils/helpers/security';
import { ConfigService } from './app-config.service';
import { MessageService } from "./message.service";
import { SystemService } from './system.service';

export type InteralStateType = { [key: string]: any };

@Injectable()
export class AppService {

  public logOut: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public startIdleCount: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public sessionEnded: BehaviorSubject<any> = new BehaviorSubject<any>(false);
  public userActivity: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  public lightMode: boolean = false;
  public lightMode$ = new Subject<any>();
  public errorFiltroObligatorio: string;

  public headerAuthentication: string;
  public headerEncrypt: string;
  public headerValueJWE: string;
  public headerValueAES: string;
  public headerValueRSA: string;
  public headerNoSpinner: string;
  public headerContentType: string;
  public headerLanguage: string;
  public noAutorizado = false;

  constructor(private http: HttpClient, private configService: ConfigService, @Inject(DOCUMENT) private document: Document, private translate: TranslateService, private systemService: SystemService, private messageService: MessageService) {
      this.errorFiltroObligatorio = 'Selecciona al menos un filtro.';

      this.configService.config.securityAPI = this.base64ToString(this.configService.config.securityAPI);
      this.configService.config.secret = this.base64ToString(this.configService.config.secret);

      this.headerAuthentication = "Authorization";
      this.headerEncrypt = "X-Encode-Data";
      this.headerValueJWE = "SAP-ENC";
      this.headerValueAES = "AES-ENC";
      this.headerValueRSA = "RSA-ENC";
      this.headerNoSpinner = "ignoreLoadingBar";
      this.headerContentType = "Content-Type";
      this.headerLanguage = "Language";
  }

  //-- Lamadas secret servicios
  get(url: string, noHeaders: Array<HeaderEnum> = [])
    {
      return new Promise<ServiceResponse>((resolve : any, reject : any) =>
      {
        let httpOptions = {headers: this.setHeader(noHeaders), observe: 'response' as 'response'};

        this.http.get(url, httpOptions).subscribe(
        {
          next: (response: HttpResponse<any>) =>
          {
            let message = response.body.message;
            let token = response.body.data;
            let status = response.body.status;

            switch (status)
            {
              case ResponseStatusEnum.OK:

                let datos : any = null;
                if (token != null)
                {
                  datos = this.decrypt(token);
                }

                resolve({status: status, message: message, data: datos});
                break;

              default:
                // No mostrar mensaje automáticamente, dejar que el componente decida
                reject({ status: status, message: message, data: null });
            }
          },
          error: (error: HttpErrorResponse) =>
          {
            if (error.status == ResponseStatusEnum.UNAUTHORIZED)
            {
              this.Unauthorized(url);
            }
            else
            {
              // No mostrar mensaje automáticamente, dejar que el componente decida
              reject({ status: error.status, message: "ErrorConexion", data: null });
            }
          }
        })
      });
    }

  /**
   * Método GET especial que devuelve tanto el cuerpo como los headers de la respuesta
   * Útil para operaciones que necesitan información de paginación en los headers
   * @param url URL de la petición
   * @param noHeaders Headers a excluir
   * @returns Promise con la respuesta completa incluyendo headers
   */
  getWithHeaders(url: string, noHeaders: Array<HeaderEnum> = [])
    {
      return new Promise<any>((resolve : any, reject : any) =>
      {
        let httpOptions = {headers: this.setHeader(noHeaders), observe: 'response' as 'response'};

        this.http.get(url, httpOptions).subscribe(
        {
          next: (response: HttpResponse<any>) =>
          {
            let message = response.body.message;
            let token = response.body.data;
            let status = response.body.status;

            switch (status)
            {
              case ResponseStatusEnum.OK:

                let datos : any = null;
                if (token != null)
                {
                  datos = this.decrypt(token);
                }

                // Devolver respuesta completa con headers
                resolve({
                  status: status,
                  message: message,
                  data: datos,
                  headers: response.headers
                });
                break;

              default:
                // No mostrar mensaje automáticamente, dejar que el componente decida
                reject({ status: status, message: message, data: null });
                break;
            }
          },
          error: (error: HttpErrorResponse) =>
          {
            if (error.status == ResponseStatusEnum.UNAUTHORIZED)
            {
              this.Unauthorized(url);
            }
            else
            {
              // No mostrar mensaje automáticamente, dejar que el componente decida
              reject({ status: error.status, message: "ErrorConexion", data: null });
            }
          }
        })
      });
    }

  post(url: string, data: any, noHeaders: Array<HeaderEnum> = [])
  {
    return new Promise<ServiceResponse>((resolve, reject) =>
    {
      let httpOptions = {headers: this.setHeader(noHeaders), observe: 'response' as 'response'};
      let cuerpo = this.encrypt(data);

      this.http.post( url, cuerpo, httpOptions).subscribe(
      {
        next : (response: HttpResponse<any>) =>
        {
          let message = response.body?.message;
          let token = response.body?.data;
          let status = response.body?.status;

          switch (status)
          {
            case ResponseStatusEnum.OK:

              let datos: any = null;
              if (token != null) {
                datos = this.decrypt(token);
              }

              resolve({status: status, message: message, data: datos});
              break;

            default:
              // No mostrar mensaje automáticamente, dejar que el componente decida
              reject({ status: status, message: message, data: null });
              break;
          }
        },
        error : (error: HttpErrorResponse) =>
        {
          if (error.status == ResponseStatusEnum.UNAUTHORIZED) {
            this.Unauthorized(url);
          } else {
            // No mostrar mensaje automáticamente, dejar que el componente decida
            reject({ status: error.status, message: "ErrorConexion", data: null });
          }
        }
     })
    });
  }

  put(url: string, data: any, noHeaders: Array<HeaderEnum> = []) : Promise<ServiceResponse>
  {
    return new Promise<ServiceResponse>((resolve, reject) =>
    {
      let httpOptions = {headers: this.setHeader(noHeaders), observe: 'response' as 'response'};
      let cuerpo = this.encrypt(data);

      this.http.put(url, cuerpo, httpOptions).subscribe(
      {
        next: (response: HttpResponse<any>) =>
        {
          let message = response.body?.message;
          let token = response.body?.data;
          let status = response.body?.status;

          switch (status)
          {
            case ResponseStatusEnum.OK:
              let datos : any = null;
              if (token != null)
              {
                datos = this.decrypt(token);
              }

              resolve({status: status, message: message, data: datos});
              break;

            default:
              // No mostrar mensaje automáticamente, dejar que el componente decida
              reject({ status: status, message: message, data: null });
              break;
          }
        },
        error: (error: HttpErrorResponse) =>
        {
          if (error.status == ResponseStatusEnum.UNAUTHORIZED)
          {
            this.Unauthorized(url);
          }
          else
          {
            // No mostrar mensaje automáticamente, dejar que el componente decida
            reject({ status: error.status, message: "ErrorConexion", data: null });
          }
        }
      })
    });
  }

  delete(url: string, noHeaders: Array<HeaderEnum> = [])
  {
    return new Promise<ServiceResponse>((resolve, reject) =>
    {
      let httpOptions = {headers: this.setHeader(noHeaders), observe: 'response' as 'response'};

      this.http.delete(url, httpOptions).subscribe(
      {
        next: (response: HttpResponse<any>) =>
        {
          let message = response.body?.message;
          let status = response.body?.status;

          switch (status)
          {
            case ResponseStatusEnum.OK:
              resolve({status: status, message: message, data: {}});
              break;

            default:
              // No mostrar mensaje automáticamente, dejar que el componente decida
              reject({ status: status, message: message, data: null });
              break;
          }
        },
        error : (error: HttpErrorResponse) =>
        {
          if (error.status == ResponseStatusEnum.UNAUTHORIZED)
          {
            this.Unauthorized(url);
          }
          else
          {
            this.HandleError()
          }

          reject();
        }
      })
    });
  }

  Unauthorized(url: string)
  {
    if (!this.noAutorizado)
    {
      this.noAutorizado = true;

      this.logOut.next(url);
      this.messageService.alert(AlertEnum.Warning, this.translate.instant("SesionCaducada"));
      this.systemService.menuOptions = [];

      setTimeout(() =>
      {
        this.noAutorizado = false;
      }, 30000);
    }
  }

  getPaginated(url: string, noHeaders: Array<HeaderEnum> = []): Observable<any>
  {
      if (noHeaders.length === 0)
      {
          noHeaders.push(HeaderEnum.Encryption);
      }

      return this.http.get<any>(url, { observe: 'response', headers: this.setHeader(noHeaders) }).pipe(tap((response: any) => {

          if (response.status == ResponseStatusEnum.UNAUTHORIZED) {
              this.logOut.next(url);
          }
      }));
  }

  setHeader(encabezados: Array<HeaderEnum>): HttpHeaders
  {
      let headers = new HttpHeaders({});

      if (localStorage.getItem(this.systemService.localStoreVariable.JWT))
      {
          headers = headers.set(this.headerAuthentication, encodeURIComponent(localStorage.getItem(this.systemService.localStoreVariable.JWT) as string));
      }

      headers = headers.set(environment.encryptionType, '');
      headers = headers.set('Content-Type', 'application/json');
      headers = headers.set(this.headerLanguage, this.translate.currentLang);

      encabezados.forEach(encabezado => {
          switch (encabezado) {
              case HeaderEnum.NoShowLoading:
                  headers = headers.set(this.headerNoSpinner, '');
                  break;

              case HeaderEnum.NoAuthenticate:
                  headers = headers.delete(this.headerAuthentication);
                  break;
              case HeaderEnum.Encryption:
                  // headers = headers.delete(this.headerValueAES);
                  break;
              default:
                  break;
          }
      });

      return headers;
  }

  encrypt(data: any) {

      switch (environment.encryptionType) {
          case 'AES-ENC':
              let security: Security = new Security();
              let encrypted = security.encryptAES(data, this.base64ToString(environment.aesKey));
              // let decrypted = security.decryptAES('eyJEYXRhIjoibjFzVW9kNmoxeEFVaXFTT2dRQWNxMUc1VjB6WXVIa2xyOC84NFQxMWl5V1pBazJhME5xcnFMRUJuRXZIWFVOQi9TMlVKYVlXemdvQWFHcGs1bUlGNm56dlVhK3lNU3NkV0ZkZTcxU3paTXNSUlhJcWx6TG5rR1g0VTczaXhjUnBmMnBhS08ySWoyZmtmaXdTcEJwSmJ2S2IyOGVJVmpFUXByd0tFbjRCa3JCVDAvdllYMjFuYU5VWWF6dkd1R2diT2FEUVVZczdHYnFWQWdIQjJpaXFxWDJTKy9qcTlsVWVZcHJjTzdEbDFrWXh1RnJZWk5FRkxGKzFDWjRSY29QQTlCNzNCdm8yRm5VU0hZOUZQK3drUnpvdXRlZHhLVGd0YXNqbC9HMk1DWlJpVjB6dTM4Y3krTTdhTGtJa2ZncmoySHBSZCt2ZURoYS9YNVBxMHE5M290T09MOHRZTTROSjJnQy9YVER1aGw0PSJ9', this.base64ToString(environment.aesKey));

              return encrypted;
          case 'RSA-ENC':
              return data;
          case 'JWT-ENC':
              return data;
          case 'JWE-ENC':
              return this.encryptData(data);
          default:
              return this.encryptData(data);
      }

  }

  // Método específico para encriptar strings simples (como fechas) sin JSON.stringify
  encryptString(data: string) {
      switch (environment.encryptionType) {
          case 'AES-ENC':
              let security: Security = new Security();
              // Encriptar directamente el string sin JSON.stringify
              let iv = cryptoJS.lib.WordArray.create([0x00, 0x00, 0x00, 0x00]);
              const encrypted = cryptoJS.AES.encrypt(data, cryptoJS.enc.Utf8.parse(this.base64ToString(environment.aesKey)), { iv });
              return encrypted.toString();
          default:
              return this.encrypt(data);
      }
  }

  //-- Encriptar los datos de LandingPage
  encryptData(datos: any) {
      let header =
      {
          alg: "HS256",
          typ: "JWT"
      }

      let stringifiedHeader = cryptoJS.enc.Utf8.parse(JSON.stringify(header));
      let encodedHeader = this.base64url(stringifiedHeader);

      let stringifiedData = cryptoJS.enc.Utf8.parse(JSON.stringify(datos));
      let encodedData = this.base64url(stringifiedData);

      let token = encodedHeader + "." + encodedData;

      let signature = cryptoJS.HmacSHA256(token, this.configService.config.secret);
      let signatureBase64 = this.base64url(signature);

      return token + "." + signatureBase64;
  }

  base64url(source: any) {
      let encodedSource = cryptoJS.enc.Base64.stringify(source);

      encodedSource = encodedSource.replace(/=+$/, '');

      encodedSource = encodedSource.replace(/\+/g, '-');
      encodedSource = encodedSource.replace(/\//g, '_');

      return encodedSource;
  }

  base64ToString(cadena: any) {
      const words = cryptoJS.enc.Base64.parse(cadena);
      return cryptoJS.enc.Utf8.stringify(words);
  }

  decrypt(data: string): any
  {
      switch (environment.encryptionType) {
          case 'AES-ENC':
              let security: Security = new Security();
              let decrypted = security.decryptAES(data, this.base64ToString(environment.aesKey));
                  // Intentar parsear automáticamente
                  return this.tryParseJSON(decrypted);
              return decrypted;
          case 'RSA-ENC':
              return data;
          case 'JWT-ENC':
              return data;
          case 'JWE-ENC':
              return this.decryptData(data);
          default:
              return this.decryptData(data);
      }
  }

  private tryParseJSON(str: string): any {
      try {
          return JSON.parse(str);
      } catch (e) {
          return str; // Retorna el string original si no es JSON válido
      }
  }

  // Método específico para desencriptar strings simples (como fechas) sin JSON.parse
  decryptString(data: string): string {
      switch (environment.encryptionType) {
          case 'AES-ENC':
              // Desencriptar directamente el string sin JSON.parse
              let iv = cryptoJS.lib.WordArray.create([0x00, 0x00, 0x00, 0x00]);
              const decrypted = cryptoJS.AES.decrypt(data, cryptoJS.enc.Utf8.parse(this.base64ToString(environment.aesKey)), { iv });
              return decrypted.toString(cryptoJS.enc.Utf8);
          default:
              return this.decrypt(data);
      }
  }

  HandleError()
  {
    let mensajeError = this.translate.instant("ErrorConexion");
    this.messageService.alert(AlertEnum.Warning, mensajeError);
    return throwError(() => new Error(mensajeError));
  }

  decryptData(response: string): string {
      let decode: string = jwt_decode(response);

      if (this.encryptData(decode) == response) {
          return decode;
      }
      else {
          return "";
      }
  }

  getDarkModeSubscription() {
      return this.lightMode$.asObservable();
  }

  saveData<T>(data: T, uriApi: string, edit: boolean): Promise<ServiceResponse>
  {
    if (edit)
    {
      return this.put(uriApi, data);
    }
    else
    {
      return this.post(uriApi, data);
    }
  }

  public DescargarArchvio(route: string, noHeaders: Array<HeaderEnum> = []) : Observable<number>
  {
    return new Observable<number>((observer) => {
      // $('.loader').addClass('show');
      let httpOptions = { responseType: "blob" as "blob", reportProgress: true, observe: "events" as "events", headers: this.setHeader(noHeaders) };

      this.http.get( route, httpOptions).subscribe(
        (event: HttpEvent<any>) =>
        {
          switch (event.type)
          {
            case HttpEventType.DownloadProgress:
              // Calcular el progreso de la descarga
              if (event.total)
              {
                let downloadProgress = Math.floor((100 * event.loaded) / event.total);
                observer.next(downloadProgress);
              }
              break;

            case HttpEventType.Response:
              // Descargar el archivo cuando la respuesta esté completa
              const blob = event.body as Blob;
              const fileName = this.getFileNameFromHeader(event.headers);

              // Crear una URL para el archivo Blob y abrirlo en el navegador
              const objectUrl = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = objectUrl;
              link.download = fileName;
              link.click();

              // Liberar la URL temporal
              URL.revokeObjectURL(objectUrl);

              observer.next(100);
              break;

            default:
              break;
          }
        },
        (error: HttpErrorResponse) =>
        {
          observer.next(-1);
        });
    });

  }

  private getFileNameFromHeader(headers: any): string
  {
    const contentDisposition = headers.get('Content-Disposition');

    if (contentDisposition)
    {
      const matches = /filename[^*=]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);

      if (matches != null && matches[1])
      {
        let fileName = matches[1].replace(/['"]/g, ''); // Quitar comillas si las hay
        return fileName;
      }
    }

    return "archivo";
  }
}
